import React from 'react';
import {
  TWIN_ROLE_LABEL,
  TWIN_ROLES,
  type TwinChain,
  type TwinRole,
} from './twinChain';

const ROLE_HINT: Record<TwinRole, string> = {
  product: 'PIM product with specs — first, then linked to an owner',
  legal_entity: 'Owner / operator (legal entity)',
  asset: 'Asset App instance (the twin)',
  device: 'IoT IMEI or ingest device_id',
};

export function TwinChainPanel({
  chain,
  appName,
}: {
  chain: TwinChain;
  appName?: string | null;
}) {
  return (
    <div data-testid="twin-chain" className="flex flex-col gap-2 p-3">
      <div className="text-[11px] text-gray-500">
        Twin chain{appName ? ` · ${appName}` : ''}
        {chain.tenant_id ? ` · tenant ${chain.tenant_id}` : ' · tenant unknown (not stamped as dev)'}
      </div>
      {TWIN_ROLES.map((role) => {
        const node = chain.nodes.find((n) => n.role === role);
        const present = Boolean(node?.present);
        return (
          <section
            key={role}
            data-twin-role={role}
            data-present={present ? '1' : '0'}
            data-empty-reason={present ? '' : node?.empty_reason || ''}
            className={`rounded-md border px-3 py-2 ${
              present ? 'border-indigo-200 bg-indigo-50/60' : 'border-dashed border-gray-300 bg-gray-50'
            }`}
          >
            <h3 className="text-xs font-semibold text-gray-800">{TWIN_ROLE_LABEL[role]}</h3>
            <p className="text-[10px] text-gray-400">{ROLE_HINT[role]}</p>
            {present ? (
              <p className="mt-1 text-[12px] text-gray-800 truncate" title={node?.label}>
                {node?.label}
              </p>
            ) : (
              <p className="mt-1 text-[11px] italic text-gray-400">
                {node?.empty_reason === 'pim_product_absent'
                  ? 'Product not in PIM — not invented'
                  : 'Not linked'}
              </p>
            )}
          </section>
        );
      })}
      <div className="mt-1 space-y-1">
        {chain.edges.length === 0 ? (
          <p className="text-[10px] italic text-gray-400">No twin edges yet</p>
        ) : (
          chain.edges.map((edge) => (
            <div key={edge.id} data-twin-edge={edge.type} className="text-[10px] text-gray-500">
              {TWIN_ROLE_LABEL[edge.from_role]} — {edge.type.replace(/_/g, ' ')} → {TWIN_ROLE_LABEL[edge.to_role]}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
